import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { confirmPassword } from '../cognitoService';
import { isAllowedRedirectDomain } from '../utils/clientNames';
import Footer from '../components/Footer';
import './Login.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [redirectToClient, setRedirectToClient] = useState('');

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const redirectParam = searchParams.get('redirectToClient');
    if (emailParam) {
      setEmail(emailParam);
    }
    if (redirectParam && isAllowedRedirectDomain(redirectParam)) {
      setRedirectToClient(redirectParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await confirmPassword(email, code, newPassword);
      setMessage('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        const loginUrl = redirectToClient ? `/login?redirectToClient=${encodeURIComponent(redirectToClient)}` : '/login';
        navigate(loginUrl);
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.code === 'CodeMismatchException') {
        setError('Invalid or expired code. Please request a new one.');
      } else if (err.code === 'InvalidPasswordException') {
        setError('Password does not meet requirements. It must contain uppercase, lowercase, numbers, and be at least 8 characters.');
      } else {
        setError(err.message || 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/logo.svg" alt="Berg" className="login-logo" />
          <h2>Set New Password</h2>
          <p>Enter the code from your email and your new password.</p>
        </div>

        {message && (
          <div className="message success-message">
            {message}
          </div>
        )}

        {error && (
          <div className="message error-message">
            {error}
            {error.includes('Invalid verification code') && (
              <p style={{ marginTop: '10px', fontSize: '14px' }}>
                Note: If you signed up with Google, you cannot reset your password here. 
                Please use "Sign in with Google" on the login page.
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Reset Code</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              placeholder="Enter the code from your email"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Enter new password"
              disabled={isLoading}
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmNewPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmNewPassword"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              disabled={isLoading}
              minLength={8}
            />
          </div>

          <button
            type="submit"
            className={`login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>

          <div className="login-links">
            <Link 
              to={`/forgot-password${redirectToClient ? `?redirectToClient=${encodeURIComponent(redirectToClient)}` : ''}`} 
              className="login-link"
            >
              Request new code
            </Link>
            <Link 
              to={`/login${redirectToClient ? `?redirectToClient=${encodeURIComponent(redirectToClient)}` : ''}`} 
              className="login-link"
            >
              Back to Login
            </Link>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
}

export default ResetPassword;